import React, { useEffect, useRef } from "react";
import { Button } from "@mhome/ui";
import { useRecoilState } from "recoil";
import { activeScopeState, platformState } from "../../global-state";
import { useAppInterface } from "../../app/app-interface-provider";
import useApiCall from "../../common/api-call";

const OAuthFlow = ({
  oAuthInfo,
  onSuccess = () => {},
  onFailure = () => {},
}) => {
  const [activeScope] = useRecoilState(activeScopeState);
  const [platform] = useRecoilState(platformState);
  const appInterface = useAppInterface();
  const { apiCall } = useApiCall();
  const pollingIntervalRef = useRef(null);

  const startPolling = (relayId) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const checkResponse = await apiCall("/auth/oauth/relay/check", "POST", {
          relayId,
        });

        if (checkResponse && checkResponse.status) {
          switch (checkResponse.status) {
            case "success":
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
              onSuccess();
              break;
            case "error":
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
              onFailure();
              break;
            case "pending":
              // Continue polling
              break;
            default:
              console.warn("Unknown OAuth status:", checkResponse.status);
              break;
          }
        }
      } catch (error) {
        console.error("Error during OAuth polling:", error);
      }
    }, 2000);
  };

  useEffect(() => {
    // Cleanup polling interval when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const initOAuthFlow = async () => {
    if (oAuthInfo) {
      try {
        // Call API to get relayId with proper request data
        const initRequestData = {
          source: platform.source || "u",
          integration: oAuthInfo.integration,
          scopeId: activeScope,
        };

        const initResponse = await apiCall(
          "/auth/oauth/relay/init",
          "POST",
          initRequestData
        );

        if (!initResponse || !initResponse.relayId) {
          console.error("Failed to get relayId");
          onFailure();
          return;
        }

        const relayId = initResponse.relayId;

        const authorizationUrl = `${oAuthInfo.authUrl}?client_id=${
          oAuthInfo.clientId
        }&redirect_uri=${
          oAuthInfo.redirectUri
        }&response_type=code&access_type=offline&scope=${oAuthInfo.scopes.join(
          " "
        )}&state=${relayId}`;

        if (platform.source === "desktop" && appInterface.isAvailable()) {
          // Use app interface to open the URL in the default browser
          appInterface.handleOAuth({
            url: authorizationUrl,
            handler: {
              integration: oAuthInfo.integration,
              scopeId: activeScope,
              originUrl: window.location.href,
              timestamp: Date.now(),
            },
          });
        } else if (platform.source === "app" && appInterface.isAvailable()) {
          // Use native browser opening for Capacitor apps (Android/iOS WebView)
          // This avoids Google OAuth's "unsecure browser" policy blocking WebView
          await appInterface.openAuthUrl(authorizationUrl);
        } else {
          // Use regular window.open for web platform
          window.open(authorizationUrl, "_blank");
        }

        // Start polling to check OAuth status
        startPolling(relayId);
      } catch (error) {
        console.error("Error during OAuth initialization:", error);
        onFailure();
      }
    }
  };

  return (
    <Button
      variant="contained"
      onClick={initOAuthFlow}
      className="normal-case w-full"
      style={{ boxShadow: "none" }}
    >
      Connect
    </Button>
  );
};

export default OAuthFlow;
